import { useState } from 'react';
import type { CariTransaction, CariSummary } from '../../../lib/cariDB';

type Props = {
  summary: CariSummary;
  transactions: CariTransaction[];
  currency: string;
  onClose: () => void;
  onAddPayment: () => void;
  onAddDebt: () => void;
  onDelete: (id: string) => void;
  onUpdateClinic: (contactName: string, phone: string) => Promise<void>;
};

export default function CariDetailModal({
  summary,
  transactions,
  currency,
  onClose,
  onAddPayment,
  onAddDebt,
  onDelete,
  onUpdateClinic,
}: Props) {
  const fmt = (n: number) => `${currency}${n.toLocaleString('tr-TR')}`;

  const [editing, setEditing] = useState(false);
  const [contactName, setContactName] = useState(summary.contact_name ?? '');
  const [phone, setPhone] = useState(summary.phone ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdateClinic(contactName.trim(), phone.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContactName(summary.contact_name ?? '');
    setPhone(summary.phone ?? '');
    setEditing(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                {summary.clinic_name.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 truncate">{summary.clinic_name}</h3>
                <p className="text-sm text-gray-500">{transactions.length} işlem</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line"></i>Düzenle
                </button>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Contact Info — view mode */}
          {!editing && (summary.contact_name || summary.phone) && (
            <div className="mt-3 ml-15 flex flex-wrap gap-4">
              {summary.contact_name && (
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <i className="ri-user-line text-gray-400"></i>
                  {summary.contact_name}
                </span>
              )}
              {summary.phone && (
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <i className="ri-phone-line text-gray-400"></i>
                  {summary.phone}
                </span>
              )}
            </div>
          )}

          {!editing && !summary.contact_name && !summary.phone && (
            <div className="mt-2 ml-15">
              <button
                onClick={() => setEditing(true)}
                className="text-xs text-gray-400 hover:text-teal-600 flex items-center gap-1 transition-colors cursor-pointer"
              >
                <i className="ri-add-line"></i>İlgili kişi &amp; telefon ekle
              </button>
            </div>
          )}

          {/* Edit Form */}
          {editing && (
            <div className="mt-4 p-4 bg-teal-50 border border-teal-100 rounded-xl space-y-3">
              <p className="text-xs font-semibold text-teal-700 mb-1 flex items-center gap-1.5">
                <i className="ri-edit-line"></i>İletişim Bilgilerini Düzenle
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">İlgili Kişi</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Ad Soyad..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Telefon</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0555 000 00 00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleCancel}
                  className="px-4 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all"
                >
                  İptal
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg text-xs font-semibold hover:from-teal-600 hover:to-cyan-700 cursor-pointer whitespace-nowrap transition-all flex items-center gap-1.5 disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Kaydediliyor...
                    </>
                  ) : (
                    <><i className="ri-check-line"></i>Kaydet</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Summary Bar */}
        <div className="grid grid-cols-3 gap-px bg-gray-100 flex-shrink-0">
          {[
            { label: 'Toplam Borç', val: summary.total_borc, color: 'text-red-600', bg: 'bg-red-50', icon: 'ri-arrow-up-circle-line' },
            { label: 'Toplam Ödeme', val: summary.total_odeme, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'ri-arrow-down-circle-line' },
            {
              label: 'Bakiye',
              val: summary.bakiye,
              color: summary.bakiye > 0 ? 'text-red-600' : summary.bakiye < 0 ? 'text-emerald-600' : 'text-gray-600',
              bg: summary.bakiye > 0 ? 'bg-red-50' : summary.bakiye < 0 ? 'bg-emerald-50' : 'bg-gray-50',
              icon: 'ri-scales-line',
            },
          ].map((item) => (
            <div key={item.label} className={`${item.bg} px-4 py-4 text-center`}>
              <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1">
                <i className={`${item.icon} ${item.color} text-lg`}></i>
              </div>
              <p className="text-xs text-gray-500 mb-1">{item.label}</p>
              <p className={`text-lg font-bold ${item.color}`}>{fmt(item.val)}</p>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-3 flex gap-2 border-b border-gray-100 flex-shrink-0">
          <button
            onClick={onAddDebt}
            className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-semibold hover:bg-red-100 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-up-circle-line"></i>Borç Ekle
          </button>
          <button
            onClick={onAddPayment}
            className="px-4 py-2 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg text-sm font-semibold hover:bg-emerald-100 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-down-circle-line"></i>Ödeme Ekle
          </button>
        </div>

        {/* Transactions List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-list-3-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">Henüz işlem yok</p>
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                    tx.type === 'borc'
                      ? 'bg-red-50 border-red-100 hover:bg-red-100'
                      : 'bg-emerald-50 border-emerald-100 hover:bg-emerald-100'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      tx.type === 'borc' ? 'bg-red-100' : 'bg-emerald-100'
                    }`}>
                      <i className={`${tx.type === 'borc' ? 'ri-arrow-up-circle-line text-red-600' : 'ri-arrow-down-circle-line text-emerald-600'} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {tx.description || (tx.type === 'borc' ? 'Borç' : 'Ödeme')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(tx.date).toLocaleDateString('tr-TR')}
                        {tx.surgery_id && (
                          <span className="ml-2 px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                            Ameliyat
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-base font-bold font-mono ${tx.type === 'borc' ? 'text-red-600' : 'text-emerald-600'}`}>
                      {tx.type === 'borc' ? '+' : '-'}{fmt(tx.amount)}
                    </span>
                    <button
                      onClick={() => onDelete(tx.id)}
                      className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition-all cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
